import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { ADMIN_BUCKET, IMPORT_BATCH_SIZE, STORE_ALL_IMPORT_ROWS } from '../config';
import { useAuth } from '../context/AuthContext';
import { chunk } from '../lib/chunks';
import { loadReferences } from '../lib/data';
import { errorMessage } from '../lib/errors';
import { sha256Hex, stableStringify } from '../lib/hash';
import {
  parseImportFile,
  validateImport,
  type ParsedImportFile,
} from '../lib/normalize';
import { supabase } from '../lib/supabase';
import { formatDate, normalizeAnswer } from '../lib/text';
import type {
  Category,
  ContentPack,
  ImportValidationResult,
  Language,
  RiddleImport,
} from '../lib/types';
import { StatusBadge } from '../components/StatusBadge';

interface AnalysisState {
  file: File;
  parsed: ParsedImportFile;
  results: ImportValidationResult[];
}

export function ImportsPage() {
  const { user } = useAuth();
  const [languages, setLanguages] = useState<Language[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [packs, setPacks] = useState<ContentPack[]>([]);
  const [analysis, setAnalysis] = useState<AnalysisState | null>(null);
  const [history, setHistory] = useState<RiddleImport[]>([]);
  const [filter, setFilter] = useState<'all' | 'valid' | 'warning' | 'error'>('all');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadHistory = async () => {
    const { data, error: queryError } = await supabase
      .from('riddle_imports')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(20);
    if (queryError) throw queryError;
    setHistory((data ?? []) as RiddleImport[]);
  };

  useEffect(() => {
    void Promise.all([loadReferences(), loadHistory()])
      .then(([refs]) => {
        setLanguages(refs.languages);
        setCategories(refs.categories);
        setPacks(refs.packs);
      })
      .catch((caught) => setError(errorMessage(caught)));
  }, []);

  const summary = useMemo(() => {
    const results = analysis?.results ?? [];
    return {
      total: results.length,
      valid: results.filter((item) => item.status === 'valid').length,
      warning: results.filter((item) => item.status === 'warning').length,
      error: results.filter((item) => item.status === 'error').length,
      categories: analysis?.parsed.categories.length ?? 0,
      equivalenceRules: analysis?.parsed.equivalenceRules.length ?? 0,
    };
  }, [analysis]);

  const visibleResults = useMemo(() => {
    const rows = analysis?.results ?? [];
    if (filter === 'all') return rows.slice(0, 250);
    return rows.filter((item) => item.status === filter).slice(0, 250);
  }, [analysis, filter]);

  const analyzeFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setError('');
    setSuccess('');
    setBusy(true);
    try {
      const text = await file.text();
      const parsed = parseImportFile(file.name, text);
      const results = validateImport(
        parsed.riddles,
        { languages, categories, packs },
        parsed.categories,
      );
      setAnalysis({ file, parsed, results });
      setFilter(results.some((item) => item.status === 'error') ? 'error' : 'all');
    } catch (caught) {
      setAnalysis(null);
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
      event.target.value = '';
    }
  };

  const downloadReport = () => {
    if (!analysis) return;
    const header = ['ligne', 'id', 'statut', 'erreurs', 'avertissements'];
    const lines = analysis.results.map((item) => [
      String(item.line),
      item.normalized?.id ?? '',
      item.status,
      item.errors.join(' | '),
      item.warnings.join(' | '),
    ]);
    const csv = [header, ...lines]
      .map((row) => row.map((cell) => `"${cell.replaceAll('"', '""')}"`).join(','))
      .join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `rapport-${analysis.file.name.replace(/\.[^.]+$/, '')}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const runImport = async () => {
    if (!analysis || !user) return;
    if (summary.error > 0) {
      const proceed = window.confirm(
        `${summary.error} ligne(s) seront ignorées. Importer les ${summary.valid + summary.warning} lignes restantes ?`,
      );
      if (!proceed) return;
    }

    setBusy(true);
    setError('');
    setSuccess('');
    setProgress(1);
    setProgressLabel('Création du journal d’import…');
    let importId: string | null = null;

    try {
      const format = analysis.file.name.toLowerCase().endsWith('.jsonl') ? 'jsonl' : 'json';
      const { data: importRow, error: importError } = await supabase
        .from('riddle_imports')
        .insert({
          filename: analysis.file.name,
          format,
          status: 'importing',
          total_count: summary.total,
          valid_count: summary.valid,
          warning_count: summary.warning,
          error_count: summary.error,
          created_by: user.id,
          options: {
            store_all_import_rows: STORE_ALL_IMPORT_ROWS,
            source_size_bytes: analysis.file.size,
            category_count: summary.categories,
            equivalence_rule_count: summary.equivalenceRules,
          },
        })
        .select('*')
        .single();
      if (importError) throw importError;
      importId = importRow.id as string;

      setProgress(8);
      setProgressLabel('Archivage du fichier source…');
      const sourcePath = `imports/${importId}/${analysis.file.name}`;
      const { error: storageError } = await supabase.storage
        .from(ADMIN_BUCKET)
        .upload(sourcePath, analysis.file, {
          contentType: analysis.file.type || 'application/json',
          upsert: false,
        });
      if (storageError) throw storageError;
      await supabase.from('riddle_imports').update({ file_path: sourcePath }).eq('id', importId);

      const packMetadata = analysis.parsed.pack;
      if (packMetadata?.id) {
        setProgress(11);
        setProgressLabel('Mise à jour du pack de contenu…');
        const packUpdate: { name?: string; description: string | null } = {
          description: packMetadata.description,
        };
        if (packMetadata.title) packUpdate.name = packMetadata.title;
        const { error: packError } = await supabase
          .from('content_packs')
          .update(packUpdate)
          .eq('id', packMetadata.id);
        if (packError) throw packError;
      }

      if (analysis.parsed.categories.length > 0) {
        setProgress(13);
        setProgressLabel('Import des catégories…');
        const { error: categoryError } = await supabase
          .from('categories')
          .upsert(analysis.parsed.categories, { onConflict: 'id' });
        if (categoryError) throw categoryError;
      }

      const rowsToJournal = STORE_ALL_IMPORT_ROWS
        ? analysis.results
        : analysis.results.filter((item) => item.status !== 'valid');
      setProgress(14);
      setProgressLabel('Enregistrement du rapport de validation…');
      for (const batch of chunk(rowsToJournal, IMPORT_BATCH_SIZE)) {
        const { error: rowsError } = await supabase.from('riddle_import_rows').insert(
          batch.map((item) => ({
            import_id: importId,
            source_line: item.line,
            riddle_id: item.normalized?.id || null,
            status: item.status,
            errors: item.errors,
            warnings: item.warnings,
            raw_data: item.raw,
          })),
        );
        if (rowsError) throw rowsError;
      }

      const importable = analysis.results.filter(
        (item): item is ImportValidationResult & { normalized: NonNullable<ImportValidationResult['normalized']> } =>
          item.status !== 'error' && item.normalized !== null,
      );
      const batches = chunk(importable, IMPORT_BATCH_SIZE);

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex += 1) {
        const batch = batches[batchIndex];
        const percent = 20 + Math.round(((batchIndex + 1) / batches.length) * 70);
        setProgress(percent);
        setProgressLabel(`Import du lot ${batchIndex + 1}/${batches.length}…`);

        const riddleRows = await Promise.all(
          batch.map(async (item) => {
            const riddle = item.normalized;
            const contentHash = await sha256Hex(
              stableStringify({
                ...riddle,
                source_payload: undefined,
              }),
            );
            return {
              id: riddle.id,
              content_pack_id: riddle.content_pack_id,
              category_id: riddle.category_id,
              language_code: riddle.language_code,
              title_internal: riddle.title_internal,
              content_text: riddle.content_text,
              answer_display: riddle.answer_display,
              is_daily_eligible: riddle.is_daily_eligible,
              manual_excluded_words: riddle.manual_excluded_words,
              manual_equivalence_groups: riddle.manual_equivalence_groups,
              difficulty: riddle.difficulty,
              status: riddle.status,
              game_data: riddle.game_data,
              source_payload: riddle.source_payload,
              content_hash: contentHash,
              created_by: user.id,
              updated_by: user.id,
            };
          }),
        );

        const { error: upsertError } = await supabase
          .from('riddles')
          .upsert(riddleRows, { onConflict: 'id' });
        if (upsertError) throw upsertError;

        const ids = batch.map((item) => item.normalized.id);
        const { error: deleteAliasError } = await supabase
          .from('answer_aliases')
          .delete()
          .in('riddle_id', ids);
        if (deleteAliasError) throw deleteAliasError;

        const aliasRows = batch.flatMap((item) =>
          item.normalized.aliases.map((alias) => ({
            riddle_id: item.normalized.id,
            display: alias.display,
            normalized_display: normalizeAnswer(alias.display),
            alias_type: alias.type,
            is_full_answer: alias.is_full_answer,
          })),
        );
        if (aliasRows.length > 0) {
          const { error: aliasError } = await supabase.from('answer_aliases').insert(aliasRows);
          if (aliasError) throw aliasError;
        }
      }

      if (analysis.parsed.equivalenceRules.length > 0) {
        setProgress(93);
        setProgressLabel('Import des règles d’équivalence…');
        const packIds = Array.from(
          new Set(analysis.parsed.equivalenceRules.map((rule) => rule.content_pack_id)),
        );
        const { data: existingRules, error: existingRulesError } = await supabase
          .from('equivalence_rules')
          .select('id, content_pack_id, puzzle_id, scope, group_key')
          .in('content_pack_id', packIds);
        if (existingRulesError) throw existingRulesError;

        const identity = (rule: {
          content_pack_id: string;
          puzzle_id: string | null;
          scope: string;
          group_key: string;
        }) => [rule.content_pack_id, rule.puzzle_id ?? '', rule.scope, rule.group_key].join('\u0000');
        const existingIds = new Map(
          (existingRules ?? []).map((rule) => [identity(rule), rule.id as string]),
        );
        const uniqueRules = new Map(
          analysis.parsed.equivalenceRules.map((rule) => {
            const key = identity(rule);
            return [key, { ...rule, id: existingIds.get(key) ?? rule.id }];
          }),
        );
        for (const ruleBatch of chunk(Array.from(uniqueRules.values()), IMPORT_BATCH_SIZE)) {
          const { error: ruleError } = await supabase
            .from('equivalence_rules')
            .upsert(ruleBatch, { onConflict: 'id' });
          if (ruleError) throw ruleError;
        }
      }

      setProgress(96);
      setProgressLabel('Finalisation…');
      const { error: finishError } = await supabase
        .from('riddle_imports')
        .update({ status: 'completed', completed_at: new Date().toISOString() })
        .eq('id', importId);
      if (finishError) throw finishError;

      setProgress(100);
      setProgressLabel('Import terminé.');
      setSuccess(
        `${summary.valid + summary.warning} énigme(s), ${summary.categories} catégorie(s) et ${summary.equivalenceRules} règle(s) d’équivalence importée(s). ${summary.error} énigme(s) ignorée(s).`,
      );
      setAnalysis(null);
      await loadHistory();
    } catch (caught) {
      if (importId) {
        await supabase.from('riddle_imports').update({ status: 'failed' }).eq('id', importId);
      }
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-stack">
      <header className="page-header">
        <div>
          <p className="eyebrow">Traitement en masse</p>
          <h1>Imports</h1>
          <p>Analyse le JSON localement, corrige les erreurs, puis importe les lignes valides par lots.</p>
        </div>
      </header>

      {error && <div className="alert error">{error}</div>}
      {success && <div className="alert success">{success}</div>}

      <section className="panel upload-panel">
        <div>
          <h2>Importer un fichier</h2>
          <p className="muted">Formats pris en charge : JSON et JSONL. Le JSON peut être un tableau ou contenir une clé <code>riddles</code>, <code>puzzles</code>, <code>items</code> ou <code>data</code>.</p>
        </div>
        <label className={`file-drop ${busy ? 'disabled' : ''}`}>
          <input type="file" accept=".json,.jsonl,.ndjson,application/json" disabled={busy} onChange={analyzeFile} />
          <strong>{busy ? 'Traitement…' : 'Choisir un fichier JSON'}</strong>
          <span>Le fichier n’est envoyé à Supabase qu’au moment de l’import.</span>
        </label>
      </section>

      {busy && progress > 0 && (
        <section className="panel progress-panel">
          <div className="progress-copy"><strong>{progressLabel}</strong><span>{progress}%</span></div>
          <div className="progress-track"><div style={{ width: `${progress}%` }} /></div>
        </section>
      )}

      {analysis && (
        <>
          <section className="stat-grid compact">
            <button className={`stat-card ${filter === 'all' ? 'selected' : ''}`} onClick={() => setFilter('all')}>
              <span>Total</span><strong>{summary.total.toLocaleString('fr-FR')}</strong>
            </button>
            <button className={`stat-card ${filter === 'valid' ? 'selected' : ''}`} onClick={() => setFilter('valid')}>
              <span>Valides</span><strong>{summary.valid.toLocaleString('fr-FR')}</strong>
            </button>
            <button className={`stat-card ${filter === 'warning' ? 'selected' : ''}`} onClick={() => setFilter('warning')}>
              <span>Avertissements</span><strong>{summary.warning.toLocaleString('fr-FR')}</strong>
            </button>
            <button className={`stat-card ${filter === 'error' ? 'selected' : ''}`} onClick={() => setFilter('error')}>
              <span>Erreurs</span><strong>{summary.error.toLocaleString('fr-FR')}</strong>
            </button>
            <div className="stat-card">
              <span>Catégories détectées</span><strong>{summary.categories.toLocaleString('fr-FR')}</strong>
            </div>
            <div className="stat-card">
              <span>Règles détectées</span><strong>{summary.equivalenceRules.toLocaleString('fr-FR')}</strong>
            </div>
          </section>

          <section className="panel table-panel">
            <div className="panel-heading">
              <div>
                <p className="eyebrow">Analyse</p>
                <h2>{analysis.file.name}</h2>
              </div>
              <div className="header-actions">
                <button className="button secondary" onClick={downloadReport}>Télécharger le rapport</button>
                <button className="button primary" disabled={busy || summary.valid + summary.warning === 0} onClick={() => void runImport()}>
                  Importer les lignes valides
                </button>
              </div>
            </div>
            <div className="table-scroll">
              <table>
                <thead><tr><th>Ligne</th><th>Identifiant</th><th>Statut</th><th>Problèmes détectés</th></tr></thead>
                <tbody>
                  {visibleResults.map((item) => (
                    <tr key={item.line}>
                      <td>{item.line}</td>
                      <td>{item.normalized?.id || '—'}</td>
                      <td><StatusBadge status={item.status} /></td>
                      <td>
                        {item.errors.length === 0 && item.warnings.length === 0 ? (
                          'Aucun'
                        ) : (
                          <>
                            {item.errors.length > 0 && (
                              <div><strong>Erreurs :</strong> {item.errors.join(' · ')}</div>
                            )}
                            {item.warnings.length > 0 && (
                              <div><strong>Avertissements :</strong> {item.warnings.join(' · ')}</div>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(analysis.results.length > 250 || visibleResults.length >= 250) && (
              <p className="table-note">Aperçu limité aux 250 premières lignes du filtre. Le rapport CSV contient toutes les lignes.</p>
            )}
          </section>
        </>
      )}

      <section className="panel table-panel">
        <div className="panel-heading"><div><p className="eyebrow">Historique</p><h2>Derniers imports</h2></div></div>
        <div className="table-scroll">
          <table>
            <thead><tr><th>Fichier</th><th>Date</th><th>Statut</th><th>Total</th><th>Valides</th><th>Avertissements</th><th>Erreurs</th></tr></thead>
            <tbody>
              {history.length === 0 ? (
                <tr><td className="empty-cell" colSpan={7}>Aucun import enregistré.</td></tr>
              ) : history.map((item) => (
                <tr key={item.id}>
                  <td><strong>{item.filename}</strong><span className="row-subtitle">{item.id}</span></td>
                  <td>{formatDate(item.created_at)}</td>
                  <td><StatusBadge status={item.status} /></td>
                  <td>{item.total_count.toLocaleString('fr-FR')}</td>
                  <td>{item.valid_count.toLocaleString('fr-FR')}</td>
                  <td>{item.warning_count.toLocaleString('fr-FR')}</td>
                  <td>{item.error_count.toLocaleString('fr-FR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
